import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';

import { type OptionType } from '@/api/common';
import { useRevenueRanges } from '@/api/settings';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  type ComboboxProps,
} from '@/components/Form/pickers';

type Props = ComboboxProps<OptionType, true> & {
  disabled?: boolean;
  className?: string;
  value?: OptionType[];
};

const RevRangeMultiSelect = ({
  value,
  size,
  disabled,
  className,
  'aria-invalid': ariaInvalid,
  ...rest
}: Props) => {
  const { revRanges, isLoading } = useRevenueRanges();
  const revRangesById = useMemo(
    () => new Map((revRanges ?? []).map((revRange) => [revRange.id, revRange])),
    [revRanges],
  );
  const resolvedValue = useMemo(() => {
    if (!value?.length) {
      return [];
    }

    return value
      .map((revRange) => revRangesById.get(revRange.id) ?? revRange)
      .filter((revRange) => Boolean(revRange.name));
  }, [revRangesById, value]);
  return (
    <Combobox
      data-testid="revrange-multiselect"
      {...rest}
      multiple
      items={isLoading ? [] : (revRanges ?? [])}
      itemToStringValue={(item) => item.id}
      itemToStringLabel={(item) => item.name ?? ''}
      isItemEqualToValue={(item, selected) => item.id === selected.id}
      isLoading={isLoading}
      disabled={disabled}
      size={size}
      value={resolvedValue}
    >
      <ComboboxChips className={className} aria-invalid={ariaInvalid}>
        <ComboboxValue>
          {resolvedValue.map((item) => (
            <ComboboxChip key={item.id}>{item.name}</ComboboxChip>
          ))}
        </ComboboxValue>
        <ComboboxChipsInput
          placeholder={isLoading ? t`Loading Revenue Sizes...` : t`Select Revenue Sizes...`}
        />
      </ComboboxChips>
      <ComboboxContent>
        <ComboboxEmpty>
          <Trans>No revenue sizes were found...</Trans>
        </ComboboxEmpty>
        <ComboboxList>
          {(item: OptionType) => (
            <ComboboxItem key={item.id} value={item} disabled={item.disabled}>
              {item.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

export default RevRangeMultiSelect;
